// GENERATED by tools/lexgen/emit-ts.mjs — DO NOT EDIT.
// Source of truth: lexicons/. Regenerate with `pnpm lex:gen`.
import { FORMATS, h3Res, isBytes, bytesLen, type FieldError, type Result, type EventResult } from "../runtime.js";

// String length convention: Unicode code points ([...s].length), so TS/Go/Rust
// agree on maxLength (Go uses rune count, Rust uses chars().count()).
function clen(s: string): number {
  return [...s].length;
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function push(errs: FieldError[], path: string, rule: string, message: string): void {
  errs.push({ path, rule, message });
}

const DEFAULT_CIPHERTEXT_CAP = 16384;

// --- string ---
function reqStr(o: Record<string, unknown>, k: string, p: string, errs: FieldError[], fmt?: string, maxLength?: number, pattern?: RegExp): void {
  if (!(k in o)) { push(errs, `${p}/${k}`, "required", `missing required field ${k}`); return; }
  optStr(o, k, p, errs, fmt, maxLength, pattern);
}
function optStr(o: Record<string, unknown>, k: string, p: string, errs: FieldError[], fmt?: string, maxLength?: number, pattern?: RegExp): void {
  if (!(k in o) || o[k] === undefined) return;
  const v = o[k];
  const path = `${p}/${k}`;
  if (typeof v !== "string") { push(errs, path, "type", "expected string"); return; }
  if (fmt && !FORMATS[fmt](v)) { push(errs, path, "format", `invalid ${fmt}`); return; }
  if (maxLength !== undefined && clen(v) > maxLength) { push(errs, path, "maxLength", `exceeds maxLength ${maxLength}`); }
  if (pattern && !pattern.test(v)) { push(errs, path, "pattern", "does not match pattern"); }
}

// --- integer ---
function reqInt(o: Record<string, unknown>, k: string, min: number | undefined, max: number | undefined, p: string, errs: FieldError[]): void {
  if (!(k in o)) { push(errs, `${p}/${k}`, "required", `missing required field ${k}`); return; }
  optInt(o, k, min, max, p, errs);
}
function optInt(o: Record<string, unknown>, k: string, min: number | undefined, max: number | undefined, p: string, errs: FieldError[]): void {
  if (!(k in o) || o[k] === undefined) return;
  const v = o[k];
  const path = `${p}/${k}`;
  if (typeof v !== "number" || !Number.isInteger(v)) { push(errs, path, "type", "expected integer"); return; }
  if (min !== undefined && v < min) { push(errs, path, "range", `below minimum ${min}`); return; }
  if (max !== undefined && v > max) { push(errs, path, "range", `above maximum ${max}`); }
}

// --- number ---
function reqNum(o: Record<string, unknown>, k: string, min: number | undefined, max: number | undefined, p: string, errs: FieldError[], exclusiveMin?: number): void {
  if (!(k in o)) { push(errs, `${p}/${k}`, "required", `missing required field ${k}`); return; }
  optNum(o, k, min, max, p, errs, exclusiveMin);
}
function optNum(o: Record<string, unknown>, k: string, min: number | undefined, max: number | undefined, p: string, errs: FieldError[], exclusiveMin?: number): void {
  if (!(k in o) || o[k] === undefined) return;
  const v = o[k];
  const path = `${p}/${k}`;
  if (typeof v !== "number" || !Number.isFinite(v)) { push(errs, path, "type", "expected number"); return; }
  if (exclusiveMin !== undefined && !(v > exclusiveMin)) { push(errs, path, "range", `must be > ${exclusiveMin}`); return; }
  if (min !== undefined && v < min) { push(errs, path, "range", `below minimum ${min}`); return; }
  if (max !== undefined && v > max) { push(errs, path, "range", `above maximum ${max}`); }
}

// --- boolean ---
function optBool(o: Record<string, unknown>, k: string, p: string, errs: FieldError[]): void {
  if (!(k in o) || o[k] === undefined) return;
  if (typeof o[k] !== "boolean") { push(errs, `${p}/${k}`, "type", "expected boolean"); }
}

// --- array ---
function reqArr(o: Record<string, unknown>, k: string, p: string, errs: FieldError[], itemCheck: (v: unknown, path: string, errs: FieldError[]) => void, minLength?: number, maxLength?: number): void {
  if (!(k in o)) { push(errs, `${p}/${k}`, "required", `missing required field ${k}`); return; }
  optArr(o, k, p, errs, itemCheck, minLength, maxLength);
}
function optArr(o: Record<string, unknown>, k: string, p: string, errs: FieldError[], itemCheck: (v: unknown, path: string, errs: FieldError[]) => void, minLength?: number, maxLength?: number): void {
  if (!(k in o) || o[k] === undefined) return;
  const v = o[k];
  const path = `${p}/${k}`;
  if (!Array.isArray(v)) { push(errs, path, "type", "expected array"); return; }
  if (minLength !== undefined && v.length < minLength) { push(errs, path, "minLength", `fewer than ${minLength} items`); }
  if (maxLength !== undefined && v.length > maxLength) { push(errs, path, "maxLength", `more than ${maxLength} items`); }
  v.forEach((item, i) => itemCheck(item, `${path}/${i}`, errs));
}

// --- item checks (for array items) ---
function itemStr(fmt?: string): (v: unknown, path: string, errs: FieldError[]) => void {
  return (v, path, errs) => {
    if (typeof v !== "string") { push(errs, path, "type", "expected string"); return; }
    if (fmt && !FORMATS[fmt](v)) { push(errs, path, "format", `invalid ${fmt}`); }
  };
}
function itemH3Res(res: number): (v: unknown, path: string, errs: FieldError[]) => void {
  return (v, path, errs) => {
    if (typeof v !== "string") { push(errs, path, "type", "expected string"); return; }
    if (!FORMATS["h3-cell"](v)) { push(errs, path, "format", "invalid h3-cell"); return; }
    if (h3Res(v) !== res) { push(errs, path, "range", `expected h3 resolution ${res}`); }
  };
}
function itemDid(): (v: unknown, path: string, errs: FieldError[]) => void {
  return itemStr("did");
}

// --- h3-cell single field with fixed resolution ---
function reqH3(o: Record<string, unknown>, k: string, res: number, p: string, errs: FieldError[]): void {
  if (!(k in o)) { push(errs, `${p}/${k}`, "required", `missing required field ${k}`); return; }
  const v = o[k];
  const path = `${p}/${k}`;
  if (typeof v !== "string") { push(errs, path, "type", "expected string"); return; }
  if (!FORMATS["h3-cell"](v)) { push(errs, path, "format", "invalid h3-cell"); return; }
  if (h3Res(v) !== res) { push(errs, path, "range", `expected h3 resolution ${res}`); }
}

// --- sealed refs with ciphertext cap ---
function checkSealed(v: unknown, path: string, errs: FieldError[], cap: number, kind: "broadcast" | "needToKnow"): void {
  if (!isObj(v)) { push(errs, path, "type", "expected object"); return; }
  if (!("ciphertext" in v)) { push(errs, `${path}/ciphertext`, "required", "missing required field ciphertext"); }
  else if (!isBytes(v.ciphertext)) { push(errs, `${path}/ciphertext`, "type", "expected bytes"); }
  else { const n = bytesLen(v.ciphertext); if (n < 0) { push(errs, `${path}/ciphertext`, "format", "invalid base64"); } else if (n > cap) { push(errs, `${path}/ciphertext`, "maxBytes", `ciphertext exceeds ${cap} bytes`); } }
  if (kind === "broadcast") {
    if (!("keyRef" in v)) { push(errs, `${path}/keyRef`, "required", "missing required field keyRef"); }
    else if (typeof v.keyRef !== "string") { push(errs, `${path}/keyRef`, "type", "expected string"); }
    else if (clen(v.keyRef) > 256) { push(errs, `${path}/keyRef`, "maxLength", "exceeds maxLength 256"); }
  } else {
    if (!("recipients" in v)) { push(errs, `${path}/recipients`, "required", "missing required field recipients"); }
    else if (!Array.isArray(v.recipients)) { push(errs, `${path}/recipients`, "type", "expected array"); }
    else { v.recipients.forEach((r, i) => itemDid()(r, `${path}/recipients/${i}`, errs)); }
  }
}
function reqSealedBroadcast(o: Record<string, unknown>, k: string, cap: number, p: string, errs: FieldError[]): void {
  if (!(k in o)) { push(errs, `${p}/${k}`, "required", `missing required field ${k}`); return; }
  checkSealed(o[k], `${p}/${k}`, errs, cap, "broadcast");
}
function optSealedBroadcast(o: Record<string, unknown>, k: string, cap: number, p: string, errs: FieldError[]): void {
  if (!(k in o) || o[k] === undefined) return;
  checkSealed(o[k], `${p}/${k}`, errs, cap, "broadcast");
}
function reqSealedNeedToKnow(o: Record<string, unknown>, k: string, cap: number, p: string, errs: FieldError[]): void {
  if (!(k in o)) { push(errs, `${p}/${k}`, "required", `missing required field ${k}`); return; }
  checkSealed(o[k], `${p}/${k}`, errs, cap, "needToKnow");
}
function optSealedNeedToKnow(o: Record<string, unknown>, k: string, cap: number, p: string, errs: FieldError[]): void {
  if (!(k in o) || o[k] === undefined) return;
  checkSealed(o[k], `${p}/${k}`, errs, cap, "needToKnow");
}

// --- mediaRef (E1 photo spec §4): content-addressed encrypted-photo reference ---
function checkMediaRef(v: unknown, path: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, path, "type", "expected object"); return; }
  reqStr(v, "blob", path, errs, undefined, 256);
  optStr(v, "thumb", path, errs, undefined, 256);
  optSealedBroadcast(v, "blurhash", 128, path, errs);
  reqStr(v, "mime", path, errs);
  reqInt(v, "bytes", 0, undefined, path, errs);
}
function itemMediaRef(): (v: unknown, path: string, errs: FieldError[]) => void {
  return (v, path, errs) => checkMediaRef(v, path, errs);
}

// --- generic object array item (open schema — just checks typeof object) ---
function itemObj(): (v: unknown, path: string, errs: FieldError[]) => void {
  return (v, path, errs) => {
    if (!isObj(v)) { push(errs, path, "type", "expected object"); }
  };
}

// --- envelope ---
function validateEnvelope(v: unknown, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, "", "type", "expected object"); return; }
  reqStr(v, "id", "", errs, "tid");
  reqStr(v, "$type", "", errs, "nsid");
  reqStr(v, "identityId", "", errs, "did");
  reqStr(v, "signingKey", "", errs, "did-key");
  // sig: bytes, <= 256 decoded bytes
  if (!("sig" in v)) { push(errs, "/sig", "required", "missing required field sig"); }
  else if (!isBytes(v.sig)) { push(errs, "/sig", "type", "expected bytes"); }
  else { const n = bytesLen(v.sig); if (n < 0) { push(errs, "/sig", "format", "invalid base64"); } else if (n > 256) { push(errs, "/sig", "maxBytes", "sig exceeds 256 bytes"); } }
  reqStr(v, "createdAt", "", errs, "datetime");
  optStr(v, "subject", "", errs, "tid");
  // geo
  if (!("geo" in v)) { push(errs, "/geo", "required", "missing required field geo"); }
  else { validateGeo(v.geo, "/geo", errs); }
  if (!("payload" in v)) { push(errs, "/payload", "required", "missing required field payload"); }
}

function validateGeo(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqH3(v, "cell", 8, p, errs);
  // path: array of h3-cells each res 4..7
  if (!("path" in v)) { push(errs, `${p}/path`, "required", "missing required field path"); }
  else if (!Array.isArray(v.path)) { push(errs, `${p}/path`, "type", "expected array"); }
  else {
    v.path.forEach((c, i) => {
      const path = `${p}/path/${i}`;
      if (typeof c !== "string") { push(errs, path, "type", "expected string"); return; }
      if (!FORMATS["h3-cell"](c)) { push(errs, path, "format", "invalid h3-cell"); return; }
      const r = h3Res(c);
      if (r < 4 || r > 7) { push(errs, path, "range", "h3 resolution out of range [4,7]"); }
    });
  }
  optInt(v, "accuracyM", 0, undefined, p, errs);
  if ("precise" in v && v.precise !== undefined) { checkSealed(v.precise, `${p}/precise`, errs, DEFAULT_CIPHERTEXT_CAP, "needToKnow"); }
}


// app.aid.account.contribution
function v_app_aid_account_contribution(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "beneficiary", p, errs, "did", undefined);
  reqStr(v, "rail", p, errs);
  reqStr(v, "nativeAmount", p, errs);
  reqStr(v, "nativeUnit", p, errs);
  reqNum(v, "pegValue", undefined, undefined, p, errs, undefined);
  reqStr(v, "pegCurrency", p, errs);
  reqNum(v, "hostValue", undefined, undefined, p, errs, undefined);
  reqStr(v, "hostCurrency", p, errs);
  optBool(v, "hostValueApprox", p, errs);
  optNum(v, "fxRate", undefined, undefined, p, errs, undefined);
  optStr(v, "fxAt", p, errs, "datetime", undefined);
  reqStr(v, "observedAt", p, errs, "datetime", undefined);
}

// app.aid.account.cost
function v_app_aid_account_cost(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  (() => {
    if (!("period" in v) || v["period"] === undefined) { push(errs, p + "/" + "period", "required", "missing required field " + "period"); return; }
    const o1 = v["period"];
    const pp1 = p + "/" + "period";
    if (!isObj(o1)) { push(errs, pp1, "type", "expected object"); return; }
    reqStr(o1, "start", pp1, errs, "datetime", undefined);
    reqStr(o1, "end", pp1, errs, "datetime", undefined);
  })();
  reqNum(v, "spendFiat", undefined, undefined, p, errs, undefined);
  reqStr(v, "fiatCurrency", p, errs);
  reqInt(v, "activeAccounts", 0, undefined, p, errs);
  reqStr(v, "accountsBasis", p, errs);
  optArr(v, "breakdown", p, errs, itemObj(), undefined, undefined);
}

// app.aid.account.supporter
function v_app_aid_account_supporter(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  optBool(v, "active", p, errs);
  reqStr(v, "since", p, errs, "datetime", undefined);
}

// app.aid.alert.notice
function v_app_aid_alert_notice(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "subject", p, errs, undefined, 256);
  reqStr(v, "severity", p, errs);
  reqStr(v, "kind", p, errs);
  optNum(v, "distanceKm", 0, undefined, p, errs, undefined);
  reqStr(v, "updatedAt", p, errs, "datetime", undefined);
}

// app.aid.alert.subscription
function v_app_aid_alert_subscription(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqArr(v, "cells", p, errs, itemStr("h3-cell"), 1, undefined);
  reqStr(v, "minSeverity", p, errs);
  (() => {
    if (!("push" in v) || v["push"] === undefined) { push(errs, p + "/" + "push", "required", "missing required field " + "push"); return; }
    const o1 = v["push"];
    const pp1 = p + "/" + "push";
    if (!isObj(o1)) { push(errs, pp1, "type", "expected object"); return; }
    reqStr(o1, "endpoint", pp1, errs, undefined, 2048);
    reqStr(o1, "p256dh", pp1, errs, undefined, 256);
    reqStr(o1, "auth", pp1, errs, undefined, 256);
  })();
  (() => {
    if (!("quietHours" in v) || v["quietHours"] === undefined) {  return; }
    const o1 = v["quietHours"];
    const pp1 = p + "/" + "quietHours";
    if (!isObj(o1)) { push(errs, pp1, "type", "expected object"); return; }
    reqStr(o1, "start", pp1, errs, undefined, undefined, /^([01]\d|2[0-3]):[0-5]\d$/);
    reqStr(o1, "end", pp1, errs, undefined, undefined, /^([01]\d|2[0-3]):[0-5]\d$/);
    reqStr(o1, "tz", pp1, errs, undefined, 64);
  })();
  optArr(v, "bucketLevels", p, errs, itemObj(), undefined, undefined);
  optStr(v, "snoozeUntil", p, errs, "datetime", undefined);
  reqStr(v, "locale", p, errs, "bcp47", undefined);
}

// app.aid.helpRequest.accept
function v_app_aid_help_request_accept(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "offer", p, errs, "tid", undefined);
  optSealedNeedToKnow(v, "locationGrant", 16384, p, errs);
}

// app.aid.helpRequest.amend
function v_app_aid_help_request_amend(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  optStr(v, "need", p, errs);
  optSealedBroadcast(v, "headline", 512, p, errs);
  optSealedBroadcast(v, "details", 16384, p, errs);
  optInt(v, "urgency", 1, 3, p, errs);
  optInt(v, "people", 1, undefined, p, errs);
  (() => {
    if (!("window" in v) || v["window"] === undefined) {  return; }
    const o1 = v["window"];
    const pp1 = p + "/" + "window";
    if (!isObj(o1)) { push(errs, pp1, "type", "expected object"); return; }
    optStr(o1, "from", pp1, errs, "datetime", undefined);
    optStr(o1, "until", pp1, errs, "datetime", undefined);
  })();
}

// app.aid.helpRequest.create
function v_app_aid_help_request_create(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "need", p, errs);
  reqSealedBroadcast(v, "headline", 512, p, errs);
  optSealedBroadcast(v, "details", 16384, p, errs);
  reqInt(v, "urgency", 1, 3, p, errs);
  optInt(v, "people", 1, undefined, p, errs);
  (() => {
    if (!("window" in v) || v["window"] === undefined) {  return; }
    const o1 = v["window"];
    const pp1 = p + "/" + "window";
    if (!isObj(o1)) { push(errs, pp1, "type", "expected object"); return; }
    optStr(o1, "from", pp1, errs, "datetime", undefined);
    optStr(o1, "until", pp1, errs, "datetime", undefined);
  })();
}

// app.aid.helpRequest.note
function v_app_aid_help_request_note(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqSealedNeedToKnow(v, "body", 8192, p, errs);
}

// app.aid.helpRequest.offer
function v_app_aid_help_request_offer(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  optArr(v, "tags", p, errs, itemStr(), undefined, undefined);
  optSealedBroadcast(v, "note", 16384, p, errs);
  optInt(v, "etaMinutes", 0, undefined, p, errs);
}

// app.aid.helpRequest.transition
function v_app_aid_help_request_transition(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "to", p, errs);
  optSealedBroadcast(v, "reason", 16384, p, errs);
}

// app.aid.helper.availability
function v_app_aid_helper_availability(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqArr(v, "tags", p, errs, itemStr(), 1, undefined);
  reqArr(v, "cells", p, errs, itemH3Res(7), 1, undefined);
  reqStr(v, "until", p, errs, "datetime", undefined);
  optBool(v, "autoAccept", p, errs);
}

// app.aid.helper.profile
function v_app_aid_helper_profile(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  optStr(v, "displayName", p, errs, undefined, 64);
  optSealedBroadcast(v, "displayNameSealed", 16384, p, errs);
  optSealedBroadcast(v, "skills", 16384, p, errs);
  optArr(v, "langs", p, errs, itemStr("bcp47"), undefined, undefined);
}

// app.aid.identity.declareKeys
function v_app_aid_identity_declare_keys(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "encryptionKey", p, errs, "did-key", undefined);
}

// app.aid.identity.rotateKey
function v_app_aid_identity_rotate_key(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "newKey", p, errs, "did-key", undefined);
  optStr(v, "newEncryptionKey", p, errs, "did-key", undefined);
  optStr(v, "prev", p, errs, "tid", undefined);
}

// app.aid.incident.closure
function v_app_aid_incident_closure(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  (() => {
    if (!("classification" in v) || v["classification"] === undefined) { push(errs, p + "/" + "classification", "required", "missing required field " + "classification"); return; }
    const o1 = v["classification"];
    const pp1 = p + "/" + "classification";
    if (!isObj(o1)) { push(errs, pp1, "type", "expected object"); return; }
    reqStr(o1, "category", pp1, errs);
    optStr(o1, "event", pp1, errs, undefined, 120);
  })();
  reqStr(v, "reason", p, errs);
}

// app.aid.incident.snapshot
function v_app_aid_incident_snapshot(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "subject", p, errs, undefined, 256);
  (() => {
    if (!("classification" in v) || v["classification"] === undefined) { push(errs, p + "/" + "classification", "required", "missing required field " + "classification"); return; }
    const o1 = v["classification"];
    const pp1 = p + "/" + "classification";
    if (!isObj(o1)) { push(errs, pp1, "type", "expected object"); return; }
    reqStr(o1, "category", pp1, errs);
    optStr(o1, "event", pp1, errs, undefined, 120);
    optStr(o1, "eventCode", pp1, errs, undefined, 64);
    optStr(o1, "severity", pp1, errs);
    optStr(o1, "urgency", pp1, errs);
    optStr(o1, "certainty", pp1, errs);
    optStr(o1, "responseType", pp1, errs);
  })();
  reqStr(v, "status", p, errs);
  reqNum(v, "W", 0, undefined, p, errs, undefined);
  reqNum(v, "confidence", 0, 1, p, errs, undefined);
  reqArr(v, "cells", p, errs, itemStr("h3-cell"), 1, undefined);
  optArr(v, "footprint", p, errs, itemStr("h3-cell"), undefined, 128);
  reqStr(v, "source", p, errs);
  reqInt(v, "distinctIdentities", 0, undefined, p, errs);
  optStr(v, "openedAt", p, errs, "datetime", undefined);
  optStr(v, "lastConfirmedAt", p, errs, "datetime", undefined);
  optNum(v, "currentFrpMw", 0, undefined, p, errs, undefined);
  optNum(v, "peakFrpMw", 0, undefined, p, errs, undefined);
  (() => {
    if (!("trend" in v) || v["trend"] === undefined) {  return; }
    const o1 = v["trend"];
    const pp1 = p + "/" + "trend";
    if (!isObj(o1)) { push(errs, pp1, "type", "expected object"); return; }
    optStr(o1, "intensity", pp1, errs);
    optStr(o1, "footprint", pp1, errs);
    optStr(o1, "corroboration", pp1, errs);
    optBool(o1, "worsening", pp1, errs);
  })();
  optArr(v, "contributors", p, errs, itemObj(), undefined, undefined);
  optStr(v, "closedReason", p, errs);
}

// app.aid.match.subscribe
function v_app_aid_match_subscribe(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "issuedAt", p, errs, "datetime", undefined);
}

// app.aid.match.suggestion
function v_app_aid_match_suggestion(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "subject", p, errs, undefined, 256);
  reqStr(v, "need", p, errs);
  reqInt(v, "urgency", 1, 3, p, errs);
  optNum(v, "rank", 0, undefined, p, errs, undefined);
  reqArr(v, "cells", p, errs, itemH3Res(7), 1, undefined);
  reqStr(v, "updatedAt", p, errs, "datetime", undefined);
}

// app.aid.message
function v_app_aid_message(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqSealedBroadcast(v, "text", 8192, p, errs);
  optStr(v, "lang", p, errs, "bcp47", undefined);
  optStr(v, "replyTo", p, errs, "tid", undefined);
}

// app.aid.mod.appeal
function v_app_aid_mod_appeal(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "action", p, errs, "tid", undefined);
  reqSealedBroadcast(v, "statement", 16384, p, errs);
}

// app.aid.mod.flag
function v_app_aid_mod_flag(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "subject", p, errs, "tid-or-did", undefined);
  reqStr(v, "reason", p, errs);
  optSealedBroadcast(v, "note", 16384, p, errs);
}

// app.aid.privacy.consent
function v_app_aid_privacy_consent(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "purpose", p, errs);
  reqStr(v, "action", p, errs);
  reqStr(v, "policyVersion", p, errs, undefined, 64);
}

// app.aid.privacy.shred
function v_app_aid_privacy_shred(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "scope", p, errs);
  reqStr(v, "target", p, errs, "tid-or-did", undefined);
}

// app.aid.report
function v_app_aid_report(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  (() => {
    if (!("classification" in v) || v["classification"] === undefined) { push(errs, p + "/" + "classification", "required", "missing required field " + "classification"); return; }
    const o1 = v["classification"];
    const pp1 = p + "/" + "classification";
    if (!isObj(o1)) { push(errs, pp1, "type", "expected object"); return; }
    reqStr(o1, "category", pp1, errs);
    optStr(o1, "event", pp1, errs, undefined, 120);
    optStr(o1, "eventCode", pp1, errs, undefined, 64);
    optStr(o1, "severity", pp1, errs);
    optStr(o1, "urgency", pp1, errs);
    optStr(o1, "certainty", pp1, errs);
    optStr(o1, "responseType", pp1, errs);
  })();
  reqInt(v, "severity", 1, 3, p, errs);
  optSealedBroadcast(v, "note", 4096, p, errs);
  optStr(v, "observedAt", p, errs, "datetime", undefined);
  (() => {
    if (!("provenance" in v) || v["provenance"] === undefined) {  return; }
    const o1 = v["provenance"];
    const pp1 = p + "/" + "provenance";
    if (!isObj(o1)) { push(errs, pp1, "type", "expected object"); return; }
    reqStr(o1, "feed", pp1, errs);
    reqStr(o1, "itemId", pp1, errs, undefined, 256);
    reqStr(o1, "fetchedAt", pp1, errs, "datetime", undefined);
  })();
  (() => {
    if (!("resources" in v) || v["resources"] === undefined) {  return; }
    const o1 = v["resources"];
    const pp1 = p + "/" + "resources";
    if (!isObj(o1)) { push(errs, pp1, "type", "expected object"); return; }
    optInt(o1, "ground", 0, undefined, pp1, errs);
    optInt(o1, "aerial", 0, undefined, pp1, errs);
    optInt(o1, "vehicles", 0, undefined, pp1, errs);
  })();
  optNum(v, "frpMw", 0, undefined, p, errs, undefined);
  optArr(v, "footprint", p, errs, itemStr("h3-cell"), undefined, 128);
  optArr(v, "media", p, errs, itemMediaRef(), undefined, 3);
}

// app.aid.spread.estimate
function v_app_aid_spread_estimate(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "subject", p, errs, undefined, 256);
  reqStr(v, "status", p, errs);
  optNum(v, "bearingDeg", 0, 360, p, errs, undefined);
  optNum(v, "speedKmh", 0, undefined, p, errs, undefined);
  optNum(v, "windBearingDeg", 0, 360, p, errs, undefined);
  optNum(v, "windSpeedKmh", 0, undefined, p, errs, undefined);
  optArr(v, "windConeCells", p, errs, itemStr("h3-cell"), undefined, undefined);
  reqNum(v, "confidence", 0, 1, p, errs, undefined);
  reqInt(v, "horizonMinutes", 0, undefined, p, errs);
  reqArr(v, "pathCells", p, errs, itemStr("h3-cell"), 1, undefined);
}

// app.aid.trust.credential
function v_app_aid_trust_credential(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "subject", p, errs, "did", undefined);
  reqStr(v, "role", p, errs);
  reqStr(v, "expiresAt", p, errs, "datetime", undefined);
}

// app.aid.trust.label
function v_app_aid_trust_label(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "subject", p, errs, "tid-or-did", undefined);
  reqNum(v, "weight", 0, 1, p, errs, 0);
  reqStr(v, "reason", p, errs);
  optStr(v, "expiresAt", p, errs, "datetime", undefined);
}

// app.aid.trust.revocation
function v_app_aid_trust_revocation(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "credential", p, errs, "tid", undefined);
  optStr(v, "reason", p, errs);
}

// app.aid.trust.vouch
function v_app_aid_trust_vouch(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "subject", p, errs, "did", undefined);
}

// app.aid.weather.conditions
function v_app_aid_weather_conditions(v: unknown, p: string, errs: FieldError[]): void {
  if (!isObj(v)) { push(errs, p, "type", "expected object"); return; }
  reqStr(v, "cell", p, errs, "h3-cell", undefined);
  optInt(v, "gridRes", 0, 15, p, errs);
  optStr(v, "subject", p, errs, undefined, 256);
  reqNum(v, "windFromDeg", 0, 360, p, errs, undefined);
  reqNum(v, "speedKmh", 0, undefined, p, errs, undefined);
  optNum(v, "gustKmh", 0, undefined, p, errs, undefined);
  reqStr(v, "observedAt", p, errs, "datetime", undefined);
  reqStr(v, "sampledAt", p, errs, "datetime", undefined);
  reqStr(v, "source", p, errs, undefined, 64);
}

const PAYLOAD_VALIDATORS: Record<string, (v: unknown, p: string, errs: FieldError[]) => void> = {
  "app.aid.account.contribution": v_app_aid_account_contribution,
  "app.aid.account.cost": v_app_aid_account_cost,
  "app.aid.account.supporter": v_app_aid_account_supporter,
  "app.aid.alert.notice": v_app_aid_alert_notice,
  "app.aid.alert.subscription": v_app_aid_alert_subscription,
  "app.aid.helpRequest.accept": v_app_aid_help_request_accept,
  "app.aid.helpRequest.amend": v_app_aid_help_request_amend,
  "app.aid.helpRequest.create": v_app_aid_help_request_create,
  "app.aid.helpRequest.note": v_app_aid_help_request_note,
  "app.aid.helpRequest.offer": v_app_aid_help_request_offer,
  "app.aid.helpRequest.transition": v_app_aid_help_request_transition,
  "app.aid.helper.availability": v_app_aid_helper_availability,
  "app.aid.helper.profile": v_app_aid_helper_profile,
  "app.aid.identity.declareKeys": v_app_aid_identity_declare_keys,
  "app.aid.identity.rotateKey": v_app_aid_identity_rotate_key,
  "app.aid.incident.closure": v_app_aid_incident_closure,
  "app.aid.incident.snapshot": v_app_aid_incident_snapshot,
  "app.aid.match.subscribe": v_app_aid_match_subscribe,
  "app.aid.match.suggestion": v_app_aid_match_suggestion,
  "app.aid.message": v_app_aid_message,
  "app.aid.mod.appeal": v_app_aid_mod_appeal,
  "app.aid.mod.flag": v_app_aid_mod_flag,
  "app.aid.privacy.consent": v_app_aid_privacy_consent,
  "app.aid.privacy.shred": v_app_aid_privacy_shred,
  "app.aid.report": v_app_aid_report,
  "app.aid.spread.estimate": v_app_aid_spread_estimate,
  "app.aid.trust.credential": v_app_aid_trust_credential,
  "app.aid.trust.label": v_app_aid_trust_label,
  "app.aid.trust.revocation": v_app_aid_trust_revocation,
  "app.aid.trust.vouch": v_app_aid_trust_vouch,
  "app.aid.weather.conditions": v_app_aid_weather_conditions,
};

const REQUIRES_SUBJECT: ReadonlySet<string> = new Set([
  "app.aid.helpRequest.accept",
  "app.aid.helpRequest.amend",
  "app.aid.helpRequest.note",
  "app.aid.helpRequest.offer",
  "app.aid.helpRequest.transition",
]);

export function validatePayload(nsid: string, value: unknown): Result {
  const fn = PAYLOAD_VALIDATORS[nsid];
  if (!fn) return { ok: true };
  const errs: FieldError[] = [];
  fn(value, "/payload", errs);
  return errs.length === 0 ? { ok: true } : { ok: false, errors: errs };
}

export function validateEvent(value: unknown): EventResult {
  const errs: FieldError[] = [];
  validateEnvelope(value, errs);
  const nsid = isObj(value) && typeof value["$type"] === "string" ? (value["$type"] as string) : "";
  const known = nsid in PAYLOAD_VALIDATORS;
  if (known) {
    if (REQUIRES_SUBJECT.has(nsid) && (!isObj(value) || value["subject"] === undefined)) {
      push(errs, "/subject", "required", "this $type requires an envelope subject");
    }
    const payload = isObj(value) ? value["payload"] : undefined;
    PAYLOAD_VALIDATORS[nsid](payload, "/payload", errs);
  }
  return { ok: errs.length === 0, known, errors: errs };
}
